/**
 * <buy-option> custom element.
 *
 * Owns the radio-card "Subscribe and save / One-time purchase" UI on the product
 * page. Renders baseline from Liquid; hydrates Appstle volume-discount tiers
 * (_ABConfig.bundle_rules) and loyalty perks (RSConfig.sellingPlansJson[].appstleCycles)
 * opportunistically once those globals appear.
 *
 * Responsibilities:
 *   - Single source of state (mode, plan, qty, variant).
 *   - Wires radio / dropdown / tier / qty events.
 *   - Recomputes prices via _ABConfig.getProductDiscountedPricing when available.
 *   - Updates <input name="selling_plan"> hidden input + ?selling_plan= URL param.
 *   - Updates the ATC button text from merchant templates.
 *   - Calls Appstle cart interceptor with current state.
 *   - Publishes 'buy-option-change' via pubsub.
 */

(function () {
  if (customElements.get('buy-option')) return;

  const GIFT_PRODUCT_CACHE = (window.products = window.products || {});

  /**
   * Format a price in cents to a money string using the Shopify-style money format.
   * Handles the standard tokens. Falls back to "$X.XX" if format is missing.
   */
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) cents = 0;
    const value = Math.round(cents);

    function withDelimiters(number, precision, thousands, decimal) {
      const parts = (number / 100).toFixed(precision).split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d{3})+$)/g, `$1${thousands}`);
      return parts.length > 1 ? `${intPart}${decimal}${parts[1]}` : intPart;
    }

    if (!format) return `${(value / 100).toFixed(2)}`;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ', '.');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  class BuyOption extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.productHandle = this.dataset.productHandle;
      this.productFormId = this.dataset.productFormId;
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.unitsPerPack = parseInt(this.dataset.unitsPerPack || '0', 10) || 0;
      this.packSingular = this.dataset.packSingular || 'Pack';
      this.packPlural = this.dataset.packPlural || 'Packs';
      this.tierOffLabel = this.dataset.tierOffLabel || '{{ percent }}% off';
      this.saveBadgeLabel = this.dataset.saveBadgeLabel || 'Save {{ percent }}%';
      this.oneTimeAvailable = this.dataset.oneTimeAvailable === 'true';
      this.subscriptionAvailable = this.dataset.subscriptionAvailable === 'true';
      this.showInlineQty = this.dataset.showInlineQtyDiscount === 'true';
      this.showInlinePerks = this.dataset.showInlineSubscriptionPerks === 'true';
      this.alwaysShowPerks = this.dataset.alwaysShowSubscriptionPerks === 'true';
      this.syncPricesAcrossModes = this.dataset.syncPricesAcrossModes === 'true';
      this.revealOnTiers = this.dataset.revealOnTiers === 'true';

      this.cards = {
        one_time: this.querySelector('.buy-option__card--one_time'),
        subscription: this.querySelector('.buy-option__card--subscription'),
      };
      this.hiddenSellingPlanInput = this.querySelector('[data-buy-option-selling-plan]');
      this.productForm = document.getElementById(this.productFormId);
      this.productFormEl = this.productForm ? this.productForm.closest('product-form') : null;
      this.qtyInput = this.productForm ? this.productForm.querySelector('[name="quantity"]') : null;
      this.variantIdInput = this.productForm ? this.productForm.querySelector('[name="id"]') : null;
      this.submitBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.submitBtnText = this.submitBtn ? this.submitBtn.querySelector('[data-product-form-submit-text]') : null;

      this.state = {
        selectedMode: this._inferInitialMode(),
        selectedPlanId: this._getSelectedPlanIdFromDOM(),
        quantity: this._getInitialQuantity(),
        selectedVariantId: this.dataset.variantId,
        unitPrice: parseInt(this.dataset.variantPrice, 10) || 0,
      };

      this._appstleHydrated = { bundles: false, perks: false };
      this._hydrationPolls = 0;
      this._bundleDiscountCache = null;
      this._allocations = this._readAllocations();
      this.perkTemplates = this._readPerkTemplates();

      this._sortPlanOptions();
      this._bindEvents();
      this._syncUrlParam();
      this._syncForeignSellingPlanInputs();
      this._observeForeignInputs();
      this.render();
      this._startAppstleHydrationPoll();
    }

    /**
     * Mirror the currently-selected plan into every other `selling_plan` /
     * `selling_plan_radio` input on the page (Appstle's subscription widget,
     * UPcart shims, etc.). Appstle installs a document-level submit
     * interceptor that rewrites /cart/add using ITS OWN state, and that
     * state is driven by `click` events on its radios — merely setting
     * `checked = true` programmatically isn't enough, because Appstle only
     * updates `window.appstleSelectedSellingPlan` / its internal map when
     * it sees a real click (or change event from a user interaction).
     *
     * For each radio group we find, we click the matching radio (dispatching
     * a real click event so Appstle's listeners fire), which also unchecks
     * the siblings in that group automatically. Non-radio inputs just get
     * their `value` overwritten.
     *
     * The MutationObserver below re-applies this when Appstle re-injects
     * nodes post-hydration.
     */
    _syncForeignSellingPlanInputs() {
      if (this._foreignSyncing) return;
      this._foreignSyncing = true;
      try {
        const planId =
          this.state.selectedMode === 'subscription' && this.state.selectedPlanId
            ? String(this.state.selectedPlanId)
            : '';

        if (this.hiddenSellingPlanInput && this.hiddenSellingPlanInput.value !== planId) {
          this.hiddenSellingPlanInput.value = planId;
        }

        const radioGroups = {};
        const nonRadios = [];
        document
          .querySelectorAll('input[name="selling_plan"], input[name="selling_plan_radio"]')
          .forEach((input) => {
            if (input === this.hiddenSellingPlanInput) return;
            if (input.type === 'radio') {
              (radioGroups[input.name] = radioGroups[input.name] || []).push(input);
            } else {
              nonRadios.push(input);
            }
          });

        // Click the matching radio in each group so Appstle (and anyone else)
        // treats this as a real user selection and updates internal state.
        Object.values(radioGroups).forEach((group) => {
          const preferred = group.find((r) => r.value === planId);
          if (preferred) {
            if (!preferred.checked) preferred.click();
            return;
          }
          // No exact match (likely one-time mode or a plan this widget doesn't know).
          // Prefer an empty-value radio when we're in one-time mode; otherwise, uncheck
          // whatever was checked so stale state doesn't submit.
          if (planId === '') {
            const empty = group.find((r) => r.value === '');
            if (empty && !empty.checked) empty.click();
          } else {
            group.forEach((r) => {
              if (r.checked) r.checked = false;
            });
          }
        });

        nonRadios.forEach((input) => {
          if (input.value !== planId) input.value = planId;
        });
      } finally {
        this._foreignSyncing = false;
      }
    }

    _observeForeignInputs() {
      if (!this.productForm || typeof MutationObserver === 'undefined') return;
      // Re-sync whenever Appstle injects / re-checks inputs. Guard against
      // infinite loops by checking we're not reacting to our own mutations.
      this._foreignObserver = new MutationObserver((mutations) => {
        if (this._foreignSyncing) return;
        const relevant = mutations.some((m) => {
          if (m.type === 'childList') {
            return Array.from(m.addedNodes).some(
              (n) =>
                n.nodeType === 1 &&
                (n.matches?.('input[name="selling_plan"], input[name="selling_plan_radio"]') ||
                  n.querySelector?.('input[name="selling_plan"], input[name="selling_plan_radio"]'))
            );
          }
          return m.target?.name === 'selling_plan' || m.target?.name === 'selling_plan_radio';
        });
        if (!relevant) return;
        this._foreignSyncing = true;
        try {
          this._syncForeignSellingPlanInputs();
        } finally {
          this._foreignSyncing = false;
        }
      });
      this._foreignObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'value'],
      });
    }

    /**
     * Re-order the <option>s in the plan dropdown by parsed delivery interval
     * (shortest first). Shopify returns plans in creation order across
     * selling_plan_groups, so a newly-added "Every 6 weeks" plan can sit after
     * "Every 2 months" despite being a shorter interval. We parse the option
     * text, derive a day count, and sort.
     */
    _sortPlanOptions() {
      const select = this.querySelector('[data-buy-option-plan-select]');
      if (!select) return;
      const options = Array.from(select.options);
      if (options.length < 2) return;

      const withKeys = options.map((opt, idx) => ({
        opt,
        idx,
        days: BuyOption._intervalDaysFromText(opt.textContent),
      }));

      const sorted = withKeys.slice().sort((a, b) => {
        if (a.days !== b.days) return a.days - b.days;
        return a.idx - b.idx;
      });

      const changed = sorted.some((entry, i) => entry.opt !== options[i]);
      if (!changed) return;

      // Preserve the pre-sort selection: appending options via a fragment can
      // reset <select>.selectedIndex to 0 in some browsers. Re-apply the value
      // we captured before moving the nodes.
      const preservedValue = select.value;
      const frag = document.createDocumentFragment();
      sorted.forEach((entry) => frag.appendChild(entry.opt));
      select.appendChild(frag);
      if (preservedValue) select.value = preservedValue;
    }

    static _intervalDaysFromText(text) {
      if (!text) return Number.MAX_SAFE_INTEGER;
      const lower = String(text).toLowerCase();
      const UNITS = { day: 1, week: 7, month: 30, year: 365 };
      const ADJECTIVES = {
        daily: 1,
        weekly: 7,
        fortnightly: 14,
        biweekly: 14,
        monthly: 30,
        bimonthly: 60,
        quarterly: 90,
        yearly: 365,
        annually: 365,
      };

      // "every other <unit>" / "other <unit>" == 2 × <unit>
      const other = lower.match(/\bother\s+(day|week|month|year)s?\b/);
      if (other) return 2 * UNITS[other[1]];

      // Explicit count: "every 2 weeks", "6 weeks", "1 month"
      const withCount = lower.match(/(\d+)\s*(day|week|month|year)s?/);
      if (withCount) {
        const n = parseInt(withCount[1], 10) || 1;
        return n * UNITS[withCount[2]];
      }

      // Adjective form: "Monthly", "Weekly", "Bimonthly"...
      for (const key in ADJECTIVES) {
        if (lower.includes(key)) return ADJECTIVES[key];
      }

      // Bare singular: "every week", "every month"
      const bareUnit = lower.match(/\b(day|week|month|year)\b/);
      if (bareUnit) return UNITS[bareUnit[1]];

      return Number.MAX_SAFE_INTEGER;
    }

    /* ---------------------------- init helpers ---------------------------- */

    _inferInitialMode() {
      const fromUrl = new URLSearchParams(window.location.search).get('selling_plan');
      if (fromUrl && this.subscriptionAvailable) return 'subscription';
      const defaultMode = this.dataset.defaultMode;
      if (defaultMode === 'subscription' && this.subscriptionAvailable) return 'subscription';
      if (!this.oneTimeAvailable && this.subscriptionAvailable) return 'subscription';
      return 'one_time';
    }

    _getSelectedPlanIdFromDOM() {
      const fromUrl = new URLSearchParams(window.location.search).get('selling_plan');
      if (fromUrl) return fromUrl;
      const select = this.querySelector('[data-buy-option-plan-select]');
      if (select && select.value) return select.value;
      const hidden = this.hiddenSellingPlanInput;
      if (hidden && hidden.value) return hidden.value;
      return null;
    }

    _getInitialQuantity() {
      if (!this.qtyInput) return 1;
      const q = parseInt(this.qtyInput.value, 10);
      return q > 0 ? q : 1;
    }

    /* ------------------------------ events ------------------------------- */

    _bindEvents() {
      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));

      if (this.qtyInput) {
        this._onQtyChange = this._onQtyChange.bind(this);
        this.qtyInput.addEventListener('change', this._onQtyChange);
        this.qtyInput.addEventListener('input', this._onQtyChange);
      }

      if (this.productFormEl) {
        this._onSubmit = this._onSubmit.bind(this);
        this.productFormEl.addEventListener('submit', this._onSubmit, true);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this._onVariantChange(event);
        });
      }

      // Inline +/- buttons dispatch 'change' on the input, but if the theme
      // uses a 'click' on the button, we already catch that via addEventListener.
    }

    _onClick(evt) {
      // Tier rows: some tier inputs are inside labels, so we can't rely solely on
      // change events — clicking the label fires a click bubbling from the label.
      // We handle both click (for label clicks) and change (for direct radio focus + keyboard).
      const tierEl = evt.target.closest('[data-buy-option-tier]');
      if (tierEl && tierEl.dataset.tierQty) {
        const q = parseInt(tierEl.dataset.tierQty, 10);
        if (q > 0 && q !== this.state.quantity) {
          this._setQuantity(q);
          this.render();
          this._publish();
        }
        return;
      }

      // Card-wide click: when a card is not the selected mode, clicking anywhere
      // inside it (outside already-interactive children like the header label,
      // plan dropdown, etc.) should select it. The header label's native click
      // continues to handle direct header clicks on its own.
      const card = evt.target.closest('.buy-option__card');
      if (!card || card.classList.contains('buy-option__card--selected')) return;
      if (evt.target.closest('a, button, input, select, label, [data-buy-option-plan-select]')) return;

      const radio = card.querySelector('[data-buy-option-radio]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _onChange(evt) {
      if (evt.target.matches('[data-buy-option-plan-select]')) {
        const planId = evt.target.value;
        if (planId !== this.state.selectedPlanId) {
          this.state.selectedPlanId = planId;
          if (this.state.selectedMode !== 'subscription') {
            this.state.selectedMode = 'subscription';
          }
          this._syncUrlParam();
          this.render();
          this._publish();
        }
        return;
      }

      if (evt.target.matches('[data-buy-option-radio]')) {
        const mode = evt.target.value;
        if (mode && mode !== this.state.selectedMode) {
          this.state.selectedMode = mode;
          if (mode === 'subscription' && !this.state.selectedPlanId) {
            const firstOpt = this.querySelector('[data-buy-option-plan-select] option');
            if (firstOpt) this.state.selectedPlanId = firstOpt.value;
          }
          this._syncUrlParam();
          this.render();
          this._publish();
        }
        return;
      }

      // Tier radios inside tier rows.
      if (evt.target.type === 'radio' && evt.target.name.startsWith(`BuyOptionTier-${this.sectionId}`)) {
        const q = parseInt(evt.target.value, 10);
        if (q > 0 && q !== this.state.quantity) {
          this._setQuantity(q);
          this.render();
          this._publish();
        }
      }
    }

    _onQtyChange() {
      if (!this.qtyInput) return;
      const q = parseInt(this.qtyInput.value, 10);
      if (!q || q < 1) return;
      if (q === this.state.quantity) return;
      this.state.quantity = q;
      this.render({ skipQtyInputSync: true });
      this._publish();
    }

    _onVariantChange(event) {
      const variant = event && event.variant;
      if (!variant) return;
      this.state.selectedVariantId = String(variant.id);
      this.state.unitPrice = variant.price;
      this.dataset.variantId = String(variant.id);
      this.dataset.variantPrice = String(variant.price);
      this._bundleDiscountCache = null;
      this.render();
      this._publish();
    }

    _onSubmit() {
      // Last-ditch sync in case Appstle injected/updated a selling_plan input
      // between the observer flush and form submit.
      this._syncForeignSellingPlanInputs();
      this._syncAppstleInterceptor();
    }

    /* ------------------------------ qty sync ----------------------------- */

    _setQuantity(q) {
      this.state.quantity = q;
      if (this.qtyInput) {
        this.qtyInput.value = q;
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ------------------------------ URL sync ----------------------------- */

    _syncUrlParam() {
      try {
        const url = new URL(window.location.href);
        if (this.state.selectedMode === 'subscription' && this.state.selectedPlanId) {
          url.searchParams.set('selling_plan', this.state.selectedPlanId);
        } else {
          url.searchParams.delete('selling_plan');
        }
        if (url.toString() !== window.location.href) {
          window.history.replaceState(window.history.state, '', url.toString());
        }
      } catch (e) {
        /* no-op — URL sync is best-effort */
      }

      if (this.hiddenSellingPlanInput) {
        const v = this.state.selectedMode === 'subscription' ? this.state.selectedPlanId || '' : '';
        this.hiddenSellingPlanInput.value = v;
      }

      // Mirror the selection into every foreign selling_plan input so
      // Appstle / UPcart / anyone else reads the same plan we're on.
      if (typeof this._syncForeignSellingPlanInputs === 'function') {
        this._syncForeignSellingPlanInputs();
      }
    }

    /* ------------------------- Appstle hydration ------------------------- */

    _startAppstleHydrationPoll() {
      const tryHydrate = () => {
        this._hydrationPolls += 1;
        let didWork = false;

        if (!this._appstleHydrated.bundles && window._ABConfig && Array.isArray(window._ABConfig.bundle_rules)) {
          this._appstleHydrated.bundles = true;
          didWork = true;
        }

        if (
          !this._appstleHydrated.perks &&
          (window.RSConfig || (window.RS && window.RS.Config)) &&
          (window.RSConfig || window.RS.Config).sellingPlansJson
        ) {
          this._appstleHydrated.perks = true;
          didWork = true;
        }

        if (didWork) {
          this._bundleDiscountCache = null;
          this.render();
        }

        const done = this._appstleHydrated.bundles && this._appstleHydrated.perks;
        const capped = this._hydrationPolls > 300; // ~30s at 100ms
        if (!done && !capped) {
          this._hydrationTimer = window.setTimeout(tryHydrate, 100);
        }
      };

      tryHydrate();
    }

    /* --------------------------- pricing engine -------------------------- */

    _getDiscountedUnitPrice(mode, planId, qty) {
      const base = this.state.unitPrice;
      let unitPrice = base;

      // 1) Volume-discount tier (if any tier's threshold <= current qty,
      // use the best matching tier's discount).
      const tiers = this._getBundleTiersForMode(mode);
      const matching = tiers.filter((t) => qty >= t.qty && t.discount > 0);
      const bestTier = matching.sort((a, b) => b.discount - a.discount)[0];
      if (bestTier) {
        if (bestTier.discountType === 'PERCENTAGE') {
          unitPrice = Math.round(base * (1 - bestTier.discount / 100));
        } else if (bestTier.discountType === 'FIXED_AMOUNT') {
          // Fixed amount is per-line-item; distribute across qty for per-unit view.
          const perUnitDeduction = Math.round((bestTier.discount * 100) / Math.max(qty, 1));
          unitPrice = Math.max(0, base - perUnitDeduction);
        }
      }

      // 2) Subscription plan discount, layered on top of any tier discount.
      if (mode === 'subscription' && planId) {
        const alloc = this._findAllocation(planId);
        if (alloc && alloc.price < base) {
          const planDiscountFrac = (base - alloc.price) / base;
          unitPrice = Math.round(unitPrice * (1 - planDiscountFrac));
        }
      }

      return {
        unitPrice,
        compareUnitPrice: base,
        discountPct: this._deriveDiscountPct(base, unitPrice),
      };
    }

    _findAllocation(planId) {
      if (!planId) return null;
      const key = String(planId);

      // Primary source: Liquid-rendered variant.selling_plan_allocations.
      // Always present on first paint, matches the card price exactly.
      const variantAllocs = this._allocations && this._allocations[this.state.selectedVariantId];
      if (variantAllocs && variantAllocs[key]) {
        return { price: variantAllocs[key].price, compare_at_price: variantAllocs[key].compare_at_price };
      }

      // Fallback: derive from Appstle's RSConfig if it has loaded. This path keeps
      // us working if the variant wasn't rendered (e.g., future variant switcher)
      // or if a store has plans without a Liquid allocation for some reason.
      const cfg = window.RSConfig || (window.RS && window.RS.Config);
      if (!cfg || !cfg.sellingPlansJson) return null;
      const plan = cfg.sellingPlansJson.find((p) => String(p.id).split('/').pop() === key);
      if (!plan) return null;
      const base = this.state.unitPrice;
      const type = (plan.discountType || '').toUpperCase();
      let price = base;
      if (type === 'PERCENTAGE' && plan.discountOffer > 0) {
        price = Math.round(base * (1 - plan.discountOffer / 100));
      } else if (type === 'FIXED' && plan.discountOffer > 0) {
        price = Math.max(0, base - Math.round(plan.discountOffer * 100));
      } else if (type === 'PRICE' && plan.discountOffer > 0) {
        price = Math.round(plan.discountOffer * 100);
      }
      return { price, compare_at_price: base };
    }

    _readAllocations() {
      const node = this.querySelector('[data-buy-option-allocations]');
      if (!node || !node.textContent) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    _readPerkTemplates() {
      const node = this.querySelector('[data-buy-option-perk-templates]');
      if (!node || !node.textContent) return {};
      try {
        return JSON.parse(node.textContent) || {};
      } catch (e) {
        return {};
      }
    }

    _deriveDiscountPct(base, current) {
      if (!base || base <= 0 || current >= base) return 0;
      return Math.round(((base - current) * 100) / base);
    }

    /* ----------------------- bundle tier extraction ---------------------- */

    _getBundleTiersForMode(mode) {
      if (!this.showInlineQty) return [];
      if (this._bundleDiscountCache && this._bundleDiscountCache._mode === mode) return this._bundleDiscountCache.tiers;

      const config = window._ABConfig;
      if (!config || !Array.isArray(config.bundle_rules)) return [];
      const targetAppliesOn = mode === 'subscription' ? 'SUBSCRIPTION' : 'ONE_TIME';

      let tiers = [];

      for (const rule of config.bundle_rules) {
        if (rule.bundleType !== 'VOLUME_DISCOUNT') continue;
        if (rule.status && rule.status !== 'ACTIVE') continue;
        if (rule.appliesOn && rule.appliesOn !== targetAppliesOn && rule.appliesOn !== 'BOTH') continue;
        if (!this._ruleTargetsProduct(rule)) continue;

        let parsed = [];
        try {
          parsed = typeof rule.tieredDiscount === 'string' ? JSON.parse(rule.tieredDiscount) : rule.tieredDiscount || [];
        } catch (e) {
          parsed = [];
        }

        for (const tier of parsed) {
          if (tier.discountBasedOn && tier.discountBasedOn !== 'QUANTITY') continue;
          const qty = parseInt(tier.value, 10);
          if (!qty || qty < 1) continue;
          const discount = parseFloat(tier.discount) || 0;
          const existing = tiers.find((t) => t.qty === qty);
          if (existing) {
            if (discount > existing.discount) {
              existing.discount = discount;
              existing.discountType = tier.discountType || existing.discountType;
            }
          } else {
            tiers.push({ qty, discount, discountType: tier.discountType || 'PERCENTAGE' });
          }
        }
      }

      tiers.sort((a, b) => a.qty - b.qty);

      // Ensure we have a "1 pack" row as anchor if any tier exists.
      if (tiers.length > 0 && !tiers.find((t) => t.qty === 1)) {
        tiers.unshift({ qty: 1, discount: 0, discountType: 'PERCENTAGE' });
      }

      this._bundleDiscountCache = { _mode: mode, tiers };
      return tiers;
    }

    _ruleTargetsProduct(rule) {
      const productId = this.dataset.productId;
      const variantId = this.state.selectedVariantId;

      const parse = (v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v;
        try {
          return JSON.parse(v) || [];
        } catch (e) {
          return [];
        }
      };

      const products = parse(rule.products);
      const variants = parse(rule.variants);
      const collectionData = parse(rule.collectionData);

      // Product-level match
      if (products.length > 0) {
        if (products.some((p) => String(p.productId) === String(productId))) return true;
      }
      // Variant-level match
      if (variants.length > 0) {
        if (variants.some((v) => String(v.variantId) === String(variantId))) return true;
      }
      // Collection-level match — we don't know the product's collection memberships in JS;
      // Appstle handles this internally for pricing, but for tier _display_ we only show if
      // the getProductDiscountedPricing call returns a discount at the relevant qty (verified
      // lazily via a probe below).
      if (collectionData.length > 0) {
        return this._probeCollectionMatch();
      }

      // No selector rule means "all products" — include.
      if (
        products.length === 0 &&
        variants.length === 0 &&
        collectionData.length === 0 &&
        (rule.productSelectionType === 'ALL' || !rule.productSelectionType)
      ) {
        return true;
      }

      return false;
    }

    _probeCollectionMatch() {
      if (this._collectionProbed != null) return this._collectionProbed;
      const config = window._ABConfig;
      if (!config || typeof config.getProductDiscountedPricing !== 'function') {
        this._collectionProbed = false;
        return false;
      }
      try {
        const res = config.getProductDiscountedPricing({
          variantId: this.state.selectedVariantId,
          amount: Math.max(this.state.unitPrice / 100, 0.01),
          quantity: 9999,
          sellingPlanId: null,
        });
        this._collectionProbed = !!(res && res.discountAmount > 0);
      } catch (e) {
        this._collectionProbed = false;
      }
      return this._collectionProbed;
    }

    /* --------------------------- perks extraction ------------------------ */

    _getPerksForPlan(planId) {
      if (!this.showInlinePerks || !planId) return [];
      const cfg = window.RSConfig || (window.RS && window.RS.Config);
      if (!cfg || !cfg.sellingPlansJson) return [];
      const plan = cfg.sellingPlansJson.find((p) => String(p.id).split('/').pop() === String(planId));
      if (!plan || !Array.isArray(plan.appstleCycles) || plan.appstleCycles.length === 0) return [];
      return plan.appstleCycles
        .filter((c) => c.afterCycle != null)
        .sort((a, b) => a.afterCycle - b.afterCycle);
    }

    _ensureGiftProductData(handles) {
      const missing = handles.filter((h) => h && !GIFT_PRODUCT_CACHE[h]);
      if (missing.length === 0) return Promise.resolve();
      // Shopify.routes.root is locale-prefixed (e.g. "/de/" on the German storefront).
      // Fetching via that root returns the product JSON in the current locale so that
      // gift.title is the translated name the merchant has set via Translate & Adapt.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return Promise.all(
        missing.map((h) =>
          fetch(`${root}products/${h}.js`, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (data) GIFT_PRODUCT_CACHE[h] = data;
            })
            .catch(() => null)
        )
      );
    }

    /* ------------------------------ render ------------------------------- */

    render(opts) {
      opts = opts || {};
      const { selectedMode, selectedPlanId, quantity } = this.state;

      // Card selected states + body visibility are driven by CSS via the selected class.
      ['one_time', 'subscription'].forEach((m) => {
        const card = this.cards[m];
        if (!card) return;
        const selected = m === selectedMode;
        card.classList.toggle('buy-option__card--selected', selected);
        const radio = card.querySelector('[data-buy-option-radio]');
        if (radio) radio.checked = selected;
      });

      // Per-card prices + per-serving.
      // When syncPricesAcrossModes is off (default), the UNSELECTED card renders
      // its baseline qty=1 price so qty changes don't leak discounts into the
      // other mode (avoids cannibalizing conversions).
      ['one_time', 'subscription'].forEach((m) => {
        const card = this.cards[m];
        if (!card) return;
        const planId = m === 'subscription' ? selectedPlanId : null;
        const isSelectedCard = m === selectedMode;
        const effectiveQty = this.syncPricesAcrossModes || isSelectedCard ? quantity : 1;
        const { unitPrice, compareUnitPrice, discountPct } = this._getDiscountedUnitPrice(m, planId, effectiveQty);

        const priceEl = card.querySelector('[data-buy-option-price]');
        const compareEl = card.querySelector('[data-buy-option-compare-price]');
        const perServingEl = card.querySelector('[data-buy-option-per-serving-value]');

        if (priceEl) priceEl.textContent = formatMoney(unitPrice, this.moneyFormat);
        if (compareEl) {
          if (compareUnitPrice > unitPrice) {
            compareEl.textContent = formatMoney(compareUnitPrice, this.moneyFormat);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }

        if (perServingEl && this.unitsPerPack > 0) {
          const perServing = unitPrice / this.unitsPerPack;
          perServingEl.textContent = formatMoney(perServing, this.moneyFormat);
        }

        // Save badge — only on subscription card, updates with plan change.
        if (m === 'subscription') {
          const badge = card.querySelector('[data-buy-option-badge]');
          if (badge) {
            if (discountPct > 0) {
              badge.textContent = this.saveBadgeLabel.replace('__PCT__', String(discountPct));
              badge.hidden = false;
            } else {
              badge.hidden = true;
            }
          }
        }
      });

      // Tiers: render inside whichever card matches the currently-selected mode
      // (also render inside subscription card if BOTH applies).
      this._renderTiers('one_time');
      this._renderTiers('subscription');
      if (this.revealOnTiers) this._resolveTierReveal();

      // Perks.
      this._renderPerks();

      // ATC button text.
      this._updateButtonText();

      // Qty sync: push state.quantity into the qty input (unless we're echoing its own event).
      if (!opts.skipQtyInputSync && this.qtyInput && parseInt(this.qtyInput.value, 10) !== quantity) {
        this.qtyInput.value = quantity;
      }
    }

    /**
     * One-time-only products render this block hidden (see buy-option.liquid).
     * Show it once Appstle tiers are found. If Appstle has loaded and there are
     * none, stay hidden and drop data-show-inline-qty-discount so the CSS stops
     * suppressing Appstle's own volume widget — the page then behaves as before.
     */
    _resolveTierReveal() {
      const hasTiers = this._getBundleTiersForMode('one_time').length >= 2;
      if (hasTiers) {
        this.hidden = false;
        this.dataset.showInlineQtyDiscount = 'true';
      } else if (this._appstleHydrated.bundles) {
        this.hidden = true;
        this.dataset.showInlineQtyDiscount = 'false';
      }
    }

    _renderTiers(mode) {
      const card = this.cards[mode];
      if (!card) return;
      const container = card.querySelector('[data-buy-option-tiers]');
      if (!container) return;

      const tiers = this._getBundleTiersForMode(mode);
      if (tiers.length < 2) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const fmt = this.moneyFormat;
      const base = this.state.unitPrice;
      const currentQty = this.state.quantity;

      const rows = tiers
        .map((tier) => {
          const discount = tier.discount || 0;
          const totalBase = base * tier.qty;
          let total = totalBase;
          if (tier.discountType === 'PERCENTAGE') {
            total = Math.round(totalBase * (1 - discount / 100));
          } else if (tier.discountType === 'FIXED_AMOUNT') {
            total = Math.max(0, totalBase - Math.round(discount * 100));
          }
          const label = tier.qty === 1 ? this.packSingular : this.packPlural;
          const isSelected = tier.qty === currentQty;
          const badge =
            discount > 0
              ? `<span class="buy-option__tier-badge">${this.tierOffLabel.replace(
                  '__PCT__',
                  String(Math.round(discount))
                )}</span>`
              : '';
          return `
            <label class="buy-option__tier${isSelected ? ' buy-option__tier--selected' : ''}"
              data-buy-option-tier
              data-tier-qty="${tier.qty}">
              <input type="radio"
                name="BuyOptionTier-${this.sectionId}-${mode}"
                value="${tier.qty}"
                class="buy-option__tier-input visually-hidden"
                ${isSelected ? 'checked' : ''}>
              <span class="buy-option__tier-radio" aria-hidden="true">
                <span class="buy-option__tier-dot"></span>
              </span>
              <span class="buy-option__tier-label">${tier.qty} ${label} – ${formatMoney(total, fmt)}</span>
              ${badge}
            </label>
          `;
        })
        .join('');

      container.innerHTML = rows;
      container.hidden = false;
    }

    _renderPerks() {
      const card = this.cards.subscription;
      if (!card) return;
      const container = card.querySelector('[data-buy-option-perks]');
      if (!container) return;

      const perks = this._getPerksForPlan(this.state.selectedPlanId);

      // Signature so we only rebuild innerHTML when the perk list genuinely changes.
      // Prevents the gift <img> from blinking (re-created = re-fetched) on every
      // mode toggle or qty change.
      const sig = JSON.stringify(
        perks.map((p) => [p.afterCycle, p.discountType, p.freeVariantId, p.freeProductHandle, p.value])
      );
      if (this._lastPerksSig === sig) return;
      this._lastPerksSig = sig;

      if (perks.length === 0) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      const handles = perks
        .filter((p) => p.discountType === 'FREE_PRODUCT' && p.freeProductHandle)
        .map((p) => p.freeProductHandle);

      const paint = () => {
        const html = perks.map((perk) => this._renderPerkHtml(perk)).join('');
        container.innerHTML = html;
        // Everything rendered empty (all templates blank, or every gift fetch
        // failed) — hide rather than show an empty box.
        container.hidden = html.trim().length === 0;
      };

      if (handles.length > 0) {
        this._ensureGiftProductData(handles).then(paint);
      } else {
        paint();
      }
    }

    _renderPerkHtml(perk) {
      const cycle = (perk.afterCycle || 0) + 1;
      const type = perk.discountType;
      const templates = this.perkTemplates || {};

      let img = '';
      let templateKey = '';
      const vars = { cycle: String(cycle) };

      if (type === 'FREE_PRODUCT') {
        const gift = GIFT_PRODUCT_CACHE[perk.freeProductHandle];
        const giftTitle = gift ? gift.title : '';
        // If we couldn't resolve the gift product (network error, etc.), skip
        // rendering this line rather than painting "get a FREE " with a blank.
        if (!giftTitle) return '';
        const giftImg = gift && gift.featured_image ? gift.featured_image : '';
        if (giftImg) {
          const safeImg = this._escapeHtml(giftImg);
          const safeTitle = this._escapeHtml(giftTitle);
          img = `<span class="buy-option__perk-image"><img src="${safeImg}" alt="${safeTitle}" loading="lazy"></span>`;
        }
        templateKey = 'free_product';
        vars.gift = giftTitle;
      } else if (type === 'PERCENTAGE') {
        templateKey = 'percentage';
        vars.value = String(perk.value);
      } else if (type === 'FIXED') {
        templateKey = 'fixed';
        vars.value = formatMoney(perk.value * 100, this.moneyFormat);
      } else if (type === 'PRICE') {
        templateKey = 'price';
        vars.value = formatMoney(perk.value * 100, this.moneyFormat);
      } else if (type === 'SHIPPING') {
        if (perk.value > 0) {
          templateKey = 'shipping_paid';
          vars.value = formatMoney(perk.value * 100, this.moneyFormat);
        } else {
          templateKey = 'shipping_free';
        }
      } else {
        return '';
      }

      const text = this._applyPerkTemplate(templates[templateKey], vars);
      if (!text) return '';

      return `
        <div class="buy-option__perk">
          ${img}
          <span class="buy-option__perk-text">${text}</span>
        </div>
      `;
    }

    // Converts a merchant-editable template string into safe HTML.
    // Flow: escape the whole template (so merchant-typed HTML is inert), then
    // expand **...** into the highlight span, then substitute [[token]] with
    // pre-escaped values. Token values are escaped here because the template
    // itself has already been escaped by the time we substitute.
    _applyPerkTemplate(template, vars) {
      if (!template) return '';
      const esc = this._escapeHtml;
      let out = esc(template);
      out = out.replace(/\*\*([\s\S]+?)\*\*/g, '<span class="buy-option__perk-highlight">$1</span>');
      out = out.replace(/\[\[(\w+)\]\]/g, (_, key) =>
        esc(vars[key] != null ? String(vars[key]) : '')
      );
      return out;
    }

    _escapeHtml(s) {
      return String(s == null ? '' : s).replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );
    }

    /* ----------------------------- button text --------------------------- */

    _updateButtonText() {
      if (!this.submitBtn || !this.submitBtnText) return;
      if (this.submitBtn.hasAttribute('disabled')) return;
      if (this.submitBtn.classList.contains('loading')) return;

      const form = this.productFormEl;
      if (!form) return;
      const templates = {
        one_time: form.dataset.oneTimeButtonText || 'Add to cart',
        subscription: form.dataset.subscriptionButtonText || 'Subscribe and save',
      };
      const tmpl = templates[this.state.selectedMode] || templates.one_time;

      const { unitPrice } = this._getDiscountedUnitPrice(
        this.state.selectedMode,
        this.state.selectedMode === 'subscription' ? this.state.selectedPlanId : null,
        this.state.quantity
      );
      const total = unitPrice * this.state.quantity;
      const rendered = tmpl.replace(/\[\[\s*amount\s*\]\]/g, formatMoney(total, this.moneyFormat));
      this.submitBtnText.textContent = rendered;
    }

    /* --------------------------- interceptor glue ------------------------ */

    _syncAppstleInterceptor() {
      if (typeof window.initAppstleBundlesInterceptor !== 'function') return;
      try {
        if (typeof window.disableAppstleBundlesInterceptor === 'function') {
          window.disableAppstleBundlesInterceptor({ variantId: this.state.selectedVariantId });
        }
        window.initAppstleBundlesInterceptor({
          variantId: this.state.selectedVariantId,
          quantity: this.state.quantity,
          sellingPlanId:
            this.state.selectedMode === 'subscription' && this.state.selectedPlanId ? this.state.selectedPlanId : null,
          hasMultipleVariants: false,
          isVolumeDiscountHasOtherProducts: false,
        });
      } catch (e) {
        /* swallow — interceptor sync is best-effort */
      }
    }

    /* ------------------------------ pubsub ------------------------------- */

    _publish() {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish('buy-option-change', {
        source: 'buy-option',
        selectedMode: this.state.selectedMode,
        selectedPlanId: this.state.selectedPlanId,
        quantity: this.state.quantity,
        selectedVariantId: this.state.selectedVariantId,
      });
    }

    /* ---------------------------- teardown ------------------------------- */

    disconnectedCallback() {
      if (this._variantChangeUnsubscribe) this._variantChangeUnsubscribe();
      if (this._hydrationTimer) window.clearTimeout(this._hydrationTimer);
      if (this._foreignObserver) this._foreignObserver.disconnect();
      if (this.qtyInput) {
        this.qtyInput.removeEventListener('change', this._onQtyChange);
        this.qtyInput.removeEventListener('input', this._onQtyChange);
      }
      if (this.productFormEl) {
        this.productFormEl.removeEventListener('submit', this._onSubmit, true);
      }
    }
  }

  customElements.define('buy-option', BuyOption);
})();
